import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../utils/httpRequest";
import {
  EnrolmentCertificate,
  OngoingCourse,
} from "../../stores/EnrolmentStore/Types";
import { CoursesResource } from "../../stores/CoursesStore/Types";
import { errorNotification } from "../../utils/helpers";
import Confetti from "../common/Confetti";
import CertificateStage from "../common/CertificateStage";
import CertificateFacts from "../common/CertificateFacts";
import NewCourse from "../Home/components/Courses/NewCourse";
import {
  ShieldIcon,
  StarIcon,
  CpdIcon,
  GlobeIcon,
} from "../Enrolment/Certificate/Icons";

interface Props {
  match: any;
  currentUser?: any;
}

interface State {
  certificate: EnrolmentCertificate;
  enrolment: OngoingCourse;
  relatedCourses: CoursesResource;
  loading: boolean;
}

// Light gold shades. Against the dark plum hero this reads as champagne
// celebration rather than a children's party popper.
const CONFETTI_COLOURS = [
  "#F3EC7A",
  "#F7E08C",
  "#F5C95A",
  "#F5B041",
  "#EAD08A",
  "#E9C56B",
];

class Certification extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      certificate: null,
      enrolment: null,
      relatedCourses: [],
      loading: true,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    const certificateId = this.props.match.params.id;

    return getRequest(`/api/certifications/${certificateId}.json`)
      .then((response: AxiosResponse) => {
        const { enrolment } = response.data;

        this.setState({
          certificate: response.data,
          enrolment,
          loading: false,
        });

        this.fetchRelatedCourses(response.data?.course_slug);
      })
      .catch((error) => {
        this.setState({ loading: false });

        errorNotification(
          "Error loading certificate data",
          JSON.stringify(error?.response?.data)
        );
      });
  }

  // The same suggestion logic the rest of the site uses, so a visitor who is
  // not sold on this specific course can still click a sibling.
  fetchRelatedCourses = (courseSlug?: string) => {
    if (!courseSlug) return null;

    // available=true only: this rail is pitching a course to a visitor who has
    // just verified someone else's certificate, so everything in it has to be
    // something they can buy today.
    return getRequest(`/api/courses/${courseSlug}/suggested.json?available=true`)
      .then((response: AxiosResponse) => {
        this.setState({ relatedCourses: response.data });
      })
      .catch(() => {
        // A missing rail is not worth an error message on a public page.
        this.setState({ relatedCourses: [] });
      });
  };

  render() {
    const { certificate, enrolment, relatedCourses, loading } = this.state;
    const { currentUser } = this.props;

    if (loading) {
      return (
        <div className="pubcert">
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
          <section className="pubcert-hero" />
        </div>
      );
    }

    const courseName = certificate?.course_name;
    const courseUrl = certificate?.course_url;
    const rating = certificate?.course_rating;
    const reviewsCount = certificate?.course_reviews_count;
    const cpdHours = certificate?.cpd_hours;

    return (
      <div className="pubcert">
        <section className="pubcert-hero" aria-labelledby="pubcert-h">
          {/* A shorter fall than the learner page's: this hero is tall because
              the certificate sits inside it, and confetti should settle in the
              plum above it rather than drift down across the artwork. */}
          <Confetti colours={CONFETTI_COLOURS} travel={[0.18, 0.34]} />

          <div className="pubcert-hero__inner">
            <span className="pubcert-eyebrow">
              <ShieldIcon /> Verified certificate
            </span>
            {/* The learner's name is deliberately not printed here. It is
                carried by the certificate artwork below, so the page never
                ranks for an individual's name. */}
            <h1 id="pubcert-h">{courseName}</h1>
            {certificate?.issued_on && (
              <p className="pubcert-hero__awarded">
                Awarded on{" "}
                <time dateTime={certificate?.issued_on_iso}>
                  {certificate.issued_on}
                </time>
              </p>
            )}

            {/* Name-free by design, but it keeps the challenge. Not shown to
                the person whose certificate this is -- they have already
                bought. */}
            {!certificate?.current && courseUrl && (
              <div className="pubcert-hook">
                <p>Reckon you're sharp enough?</p>
                <a className="btn btn-blue" href={courseUrl}>
                  Find out
                </a>
              </div>
            )}
          </div>

          <div className="pubcert-stage-wrap">
            <CertificateStage
              orientation="responsive"
              landscapeUrl={certificate?.landscape_url}
              portraitUrl={certificate?.portrait_url}
              alt={`42courses ${courseName} certificate`}
            />
          </div>
        </section>

        <section className="pubcert-verify" aria-labelledby="pubcert-verify-h">
          <h2 id="pubcert-verify-h" className="sr-only">
            Certificate details
          </h2>
          <CertificateFacts
            facts={[
              { label: "Issued by", value: "42courses" },
              {
                label: "Result",
                value: enrolment?.score ? `Passed, ${enrolment.score}%` : "Passed",
              },
              { label: "CPD", value: cpdHours ? `${cpdHours} hours` : "" },
              {
                label: "Credential ID",
                value: certificate?.credential_id,
                mono: true,
              },
            ]}
          />
        </section>

        <section className="pubcert-about" aria-labelledby="pubcert-about-h">
          <div className="pubcert-about__head">
            <span className="eb">The course behind the certificate</span>
            {certificate?.course_byline && (
              <h2 id="pubcert-about-h">{certificate.course_byline}</h2>
            )}
            <p>
              <strong>{courseName}</strong> is one of 40+ CPD-accredited Mini
              MBAs on 42courses, co-created with people who actually do the
              work. Yours could be next.
            </p>
          </div>

          <div className="pubcert-chips">
            {/* Rating only, no review count: a new course honestly has few
                reviews, and "4.9 from 13 reviews" undersells it next to the
                same 4.9 on a course that has been out for years. */}
            {!!rating && (
              <span className="chip">
                <StarIcon className="chip__star" /> {rating.toFixed(1)} out of 5
              </span>
            )}
            {!!cpdHours && (
              <span className="chip">
                <CpdIcon className="chip__cpd" /> CPD accredited
              </span>
            )}
            <span className="chip">
              <GlobeIcon /> 170+ countries
            </span>
          </div>

          {courseUrl && (
            <div className="pubcert-cta">
              <a className="btn btn-blue btn--lg" href={courseUrl}>
                {/* Several course names already begin with "The", so the
                    article is dropped rather than doubled up. */}
                {/^the\s/i.test(courseName || "")
                  ? `View ${courseName} course`
                  : `View the ${courseName} course`}
              </a>
              <p className="pubcert-cta__sub">
                If you are not happy, we are not happy. 30-day money-back
                guarantee.
              </p>
            </div>
          )}

          {/* facelift-landing-page is the namespace .course-card is styled
              under; without it these render as unstyled stacked blocks. The
              grid itself is this page's own, so unlike the learner page it does
              not also need facelift-course-preview. */}
          {relatedCourses.length > 0 && (
            <div className="pubcert-rail facelift-landing-page">
              <h3>More courses like this</h3>
              <div className="pubcert-rail__grid">
                {relatedCourses.map((course) => (
                  <NewCourse
                    key={course.id}
                    courseResource={course}
                    userIsInUk={currentUser?.is_in_uk}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    );
  }
}

export default Certification;
